import { useState, useContext } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import tableStyle from '../Style/TableModel.module.css';
import formStyle from '../Style/modalForm.module.css';
import button from '../Style/ButtonStyle.module.css';
import AuthContext from "../../store/auth-context";
import AddOrganisation from './AddOrganistaion';
import { checkCustomerExists, fetchEpubOrganisation } from '../../services/organization.service';

interface ImportOrganisationProps {
    modalStatus: boolean;
    onAction: () => void;
    setOrganisationList: (list: any) => void;
    organisationList: any[];
    setMessage: (message: string) => void;
}

const ImportOrganisation: React.FC<ImportOrganisationProps> = (props) => {
    const authCtx = useContext(AuthContext);
    const [custID, setCustID] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [existingOrg, setExistingOrg] = useState<any>(null);
    const [epubOrgData, setEpubOrgData] = useState<any>(null);
    const [showImportForm, setShowImportForm] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const handleContinue = async () => {
        if (!custID || custID.trim() === "") {
            toast.error("Please enter a Customer ID");
            return;
        }

        setIsLoading(true);
        setExistingOrg(null);
        setEpubOrgData(null);
        setShowImportForm(false);
        setErrorMessage("");

        try {
            // Step 1: Check if organisation exists in School Link database
            const existingOrgResponse = await checkCustomerExists(parseInt(custID), authCtx.token);
            
            if (existingOrgResponse.exists && existingOrgResponse.organisation) {
                // Organisation already exists in School Link
                setExistingOrg(existingOrgResponse.organisation);
            } else {
                // Step 2: Fetch from ePub API
                const epubResponse = await fetchEpubOrganisation(parseInt(custID), authCtx.token);
                
                if (epubResponse.error || !epubResponse.data) {
                    setErrorMessage(epubResponse.message || "Failed to find organisation.");
                } else {
                    // Map ePub data to School Link format
                    const mappedData = {
                        id: 0, // New organisation (not an update)
                        custID: epubResponse.data.cust_id,
                        epubOrgId: epubResponse.data.org_id,
                        name: epubResponse.data.org_name || "",
                        website: epubResponse.data.org_website || "",
                        phone: epubResponse.data.org_phone || "",
                        streetAddress: epubResponse.data.org_street_address || "",
                        suburb: epubResponse.data.org_suburb || "",
                        state: epubResponse.data.org_state || "",
                        postCode: epubResponse.data.org_postcode || "",
                        country: epubResponse.data.org_country || "",
                        email: epubResponse.data.org_email || "",
                        org_sendgrid_hashid: epubResponse.data.org_sendgrid_hashid || "",
                        org_sendgrid_username: epubResponse.data.org_sendgrid_username || "",
                        org_sendgrid_password: epubResponse.data.org_sendgrid_password || "",
                        org_wistia_project_id: epubResponse.data.org_wistia_project_id || "",
                        sendgrid_api_key: epubResponse.data.org_api_key || "",
                        status: "INACTIVE" as const,
                        logoUrl: "",
                        subscriptionPlan: null
                    };
                    setEpubOrgData(mappedData);
                    setShowImportForm(true);
                }
            }
        } catch (error: any) {
            console.error("Error checking customer:", error);
            setErrorMessage(error.message || "Failed to find organisation");
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseModal = () => {
        setCustID("");
        setExistingOrg(null);
        setEpubOrgData(null);
        setShowImportForm(false);
        setErrorMessage("");
        props.onAction();
    };

    const formatDate = (date: string) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleString();
    };

    return (
        <>
            <Modal
                size="lg"
                show={props.modalStatus}
                onHide={handleCloseModal}
                aria-labelledby="import-org-modal-title"
            >
                <div className={formStyle.headArea}>
                    <Modal.Title id="import-org-modal-title">
                        <h4 className={tableStyle.modalTitle}>ePublisher Organisation Migration</h4>
                    </Modal.Title>
                </div>

                {!showImportForm ? (
                    <div className={formStyle.bodyArea}>
                        <div className="row">
                            <div className="col-12">
                                <div className={formStyle.group}>
                                    <label className="form-label">Customer ID</label>
                                    <input
                                        type="number"
                                        placeholder="Enter Customer ID"
                                        className={formStyle.formControl}
                                        value={custID}
                                        onChange={e => setCustID(e.target.value)}
                                        disabled={isLoading}
                                    />
                                </div>
                                <div className="mb-3">
                                    <button
                                        onClick={handleContinue}
                                        className={button.btn + ' ' + button.blueButton}
                                        disabled={isLoading}
                                    >
                                        {isLoading ? 'Checking...' : 'Continue'}
                                    </button>
                                </div>
                                
                                {errorMessage && (
                                    <div className="alert alert-danger mt-3">
                                        {errorMessage}
                                    </div>
                                )}
                            </div>
                        </div>

                        {existingOrg && (
                            <div className="row mt-4">
                                <div className="col-12">
                                    <div className="alert alert-warning">
                                        <h5>Organisation Already Exists</h5>
                                        <p>The organisation with this customer ID already exists in SchoolLink database. Here are the details:</p>
                                        
                                        <div className="mt-3">
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <p><strong>Name:</strong> {existingOrg.name || 'N/A'}</p>
                                                    <p><strong>Customer ID:</strong> {existingOrg.custID || 'N/A'}</p>
                                                    <p><strong>UUID:</strong> {existingOrg.uuid || 'N/A'}</p>
                                                    <p><strong>Phone:</strong> {existingOrg.phone || 'N/A'}</p>
                                                </div>
                                                <div className="col-md-6">
                                                    <p><strong>State:</strong> {existingOrg.state || 'N/A'}</p>
                                                    <p><strong>Post Code:</strong> {existingOrg.postCode || 'N/A'}</p>
                                                    <p><strong>Email:</strong> {existingOrg.email || 'N/A'}</p>
                                                    <p><strong>Created:</strong> {formatDate(existingOrg.createDate)}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                        <AddOrganisation
                            modalStatus={true}
                            onAction={handleCloseModal}
                            organisationDetail={epubOrgData}
                            organisationList={props.organisationList}
                            setOrganisationList={props.setOrganisationList}
                            setOrganisationDetail={() => {}}
                            setMessage={props.setMessage}
                        />
                    </div>
                )}

                {!showImportForm && (
                    <div className={formStyle.FooterArea}>
                        <button
                            type="button"
                            onClick={handleCloseModal}
                            className={button.btn + ' ' + button.brownButton}
                        >
                            Cancel
                        </button>
                    </div>
                )}
            </Modal>
        </>
    );
};

export default ImportOrganisation;
